<script>
		import { user, fetch_data, send_data } from "../client.svelte.js";
		import { initials, categories } from "../core.js";
		import { narrate, is_admin } from "./_Narrate.svelte";

		let fshp = view.fellowship
		let admin = $derived(is_admin(fshp))
		let increases = $state(null)
		let busy = $state(false)
		let failedTokens = $state([])		// heroes without portrait image (initials are shown instead)

		/** increases grouped by hero **/
		let groups = $derived(Object.values(Object.groupBy(fshp.upgrades ?? [], incr => incr.CHAR)))

		/** accepts (verdict 'accept') or declines (verdict 'decline') the increases; the server responds with the remaining increases **/
		/** heroes of the fellowship with their expended XP by category (calculated by the server) **/
		let xpHeroes = $derived(view.fellowship.xpDetails ?? [])

		/** table rows: main categories followed by their sub-categories (only those in which XP were spent) **/
		let xpRows = $derived.by(() => {
				const used = (cat) => xpHeroes.some(hero => hero.xps[cat])
				const rows = []
				for (const main of ['G', 'C', 'M', 'B'].filter(used)) {
					rows.push({cat: main, name: (main === 'G') ? "Allgemein" : categories[main], main: true})
					for (const cat of Object.keys(categories).filter(cat => cat.length > 1 && cat[0] === main && used(cat)))
						rows.push({cat, name: categories[cat]})
				}
				return rows
		})

		const format_XP = (xp) => xp ? xp.toLocaleString('de-DE') : ""

		async function decide(incrs, verdict) {
				busy = true
				try {
					for (const incr of incrs) {
						await send_data(`/proficiency/${incr.ID}`, {ID: incr.ID, CHAR: incr.CHAR, SKILL: incr.SKILL, fshpID: fshp.ID, verdict}, 'PATCH')
					}
				} catch (err) {
					console.error(`NARRATE: increase could not be decided`, err)
				} finally {
					busy = false
				}
		}

		function decide_all(verdict) {
				const action = verdict === 'accept' ? "annehmen" : "ablehnen"
				if (confirm(`Sollen wirklich alle ${fshp.upgrades.length} Verbesserungen ${action} werden?`))
					decide([...fshp.upgrades], verdict)
		}
</script>


<!-------------------------------- HTML -------------------------------------->

<article class="improvements">
	{#if !admin}
		<p class="card-meta">Nur der Admin der Gruppe kann die Verbesserungen prüfen.</p>
	{:else}
		<div class="headRow">
			<div>
				<h2 class="page-title">Verbesserungen prüfen</h2>
				<p class="card-meta">
					{#if !fshp.upgrades}
						Die Verbesserungen werden geladen …
					{:else if fshp.upgrades.length}
						{fshp.upgrades.length} Verbesserungen warten auf Deine Entscheidung. Angenommene Werte werden in die Fertigkeiten der Helden übernommen.
					{:else}
						Alle Verbesserungen wurden bearbeitet.
					{/if}
				</p>
			</div>
			<menu class="headActions" data-help="decideall">
				<li><button class="button-secondary" disabled={busy || !fshp.upgrades?.length} onclick={() => decide_all('decline')}>alle ablehnen</button></li>
				<li><button class="btn-primary btn-primary-circle icon-check" disabled={busy || !fshp.upgrades?.length}
				            onclick={() => decide_all('accept')}>alle annehmen</button></li>
			</menu>
		</div>

		{#each groups as group (group[0].CHAR)}
			<fieldset class="heroGroup" data-help="heroupgrades">
				<div class="memberRow heroRow">
					{#if failedTokens.includes(group[0].CHAR)}
						<span class="avatar portrait">{initials(group[0].hero)}</span>
					{:else}
						<img class="portrait" src={group[0].tokenURL} alt={group[0].hero}
						     onerror={() => failedTokens.push(group[0].CHAR)}>
					{/if}
					<div class="memberName">
						<div>{group[0].hero}</div>
						<div class="card-meta">{group[0].player}</div>
					</div>
					<span class="card-meta">{group.length} offen</span>
				</div>
				{#each group as incr (incr.ID)}
					<div class="incrRow">
						<div class="skillName">
							<div>{incr.skill}</div>
							<div class="card-meta">{incr.category}</div>
						</div>
						<div class="comment">{incr.comment || "–"}</div>
						<div class="values">
							<span class="from">{incr.learned}</span>
							<span class="fa icon-chevron-right"></span>
							<span class="to">{incr.learned + incr.increase}</span>
						</div>
						<div class="rowActions">
							<button class="button-secondary" disabled={busy} onclick={() => decide([incr], 'decline')}>ablehnen</button>
							<button class="btn-primary btn-primary-circle icon-check" disabled={busy} onclick={() => decide([incr], 'accept')}>annehmen</button>
						</div>
					</div>
				{/each}
			</fieldset>
		{/each}

		{#if fshp.upgrades && !fshp.upgrades.length}
			<p class="card-meta">Für diese Gruppe liegen keine Verbesserungen vor.</p>
		{/if}

		<fieldset data-help="xptable">
			<div class="xpHead">
				<h2 class="page-title">Erfahrungspunkte der Helden</h2>
				<p class="card-meta">Verbrauchte Erfahrungspunkte je Kategorie (Fertigkeiten und Meisterschaften) sowie die noch freien Punkte.</p>
			</div>
			<div class="tableFrame">
				<table class="xpTable">
					<thead>
						<tr>
							<th class="nameCol">Kategorie</th>
							{#each xpHeroes as hero (hero.ID)}
								<th class="valCol" title={hero.ID}>
									<div>{hero.name}</div>
									<div class="owner">{hero.owner}</div>
								</th>
							{/each}
						</tr>
					</thead>
					<tbody>
						{#each xpRows as row (row.cat)}
							<tr class:mainRow={row.main}>
								<td class="nameCol" class:subName={!row.main}>{row.name}</td>
								{#each xpHeroes as hero (hero.ID)}
									<td class="valCol">{format_XP(hero.xps[row.cat])}</td>
								{/each}
							</tr>
						{/each}
						<tr class="totalRow">
							<td class="nameCol">Verbraucht gesamt</td>
							{#each xpHeroes as hero (hero.ID)}
								<td class="valCol">{format_XP(hero.xps.Total)}</td>
							{/each}
						</tr>
						<tr class="freeRow">
							<td class="nameCol">Frei</td>
							{#each xpHeroes as hero (hero.ID)}
								<td class="valCol" class:negative={hero.xps.free < 0}>{hero.xps.free.toLocaleString('de-DE')}</td>
							{/each}
						</tr>
					</tbody>
				</table>
			</div>
		</fieldset>
	{/if}
</article>


<!-------------------------------- STYLES -------------------------------------->

<style lang="sass">
	@use "../colors" as colors

	:global(#narrate) .improvements
		overflow-y: auto
		scrollbar-width: thin
		font-size: 16px

		.card-meta
			font-size: 14px

		.headRow, .heroGroup, .xpHead
			max-width: 960px

		.headRow
			display: flex
			align-items: flex-end
			justify-content: space-between
			flex-wrap: wrap
			gap: 16px
			margin-bottom: 8px

		.headActions, .rowActions
			display: flex
			align-items: center
			justify-content: flex-end
			gap: 10px
			margin: 0
			padding: 0
			list-style: none

		.heroRow
			gap: 14px
			padding: 10px 0
			border-bottom-color: colors.$primary-060-black

			.memberName div:first-child
				font-size: 20px
				font-weight: 500
				color: colors.$white

		.portrait
			flex: none
			width: 52px
			height: 52px
			border-radius: 50%
			border: 2px solid colors.$primary-060-black
			object-fit: cover
			background: colors.$grey-20

		.avatar.portrait
			font-size: 16px

		.incrRow
			display: grid
			grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr) 12ch auto
			align-items: center
			gap: 16px
			padding: 10px 0
			border-bottom: 1px solid colors.$grey-20

		.skillName
			min-width: 0
			line-height: 1.3

			div:first-child
				font-size: 16px

		.comment
			min-width: 0
			font-size: 14.5px
			color: colors.$grey-85
			text-wrap: pretty

		.values
			display: flex
			align-items: center
			gap: 8px
			font-size: 18px
			font-variant-numeric: tabular-nums

			.from
				color: colors.$grey-50

			.fa
				font-size: 12px
				color: colors.$grey-30

			.to
				font-weight: 500
				color: colors.$primary-100

		.rowActions
			min-width: 260px

			.button-secondary
				padding-block: 5px
				font-size: 15px

			.btn-primary
				min-width: 0
				height: 32px
				font-size: 15px

		/* experience table (layout as in Survey) */
		.xpHead
			margin-top: 40px

		.tableFrame
			max-width: 100%
			overflow-x: auto
			scrollbar-width: thin
			scrollbar-color: colors.$grey-30 transparent

		.xpTable
			width: auto
			min-width: 420px
			border-collapse: collapse
			font-size: 14px

			th, td
				padding: 6px 8px

			th
				text-align: left
				font-size: 11px
				letter-spacing: .08em
				text-transform: uppercase
				color: colors.$grey-60
				line-height: 1.25

			thead th
				background: colors.$black
				box-shadow: inset 0 -1px 0 colors.$grey-30

			tbody tr
				border-bottom: 1px solid colors.$grey-20

				&:hover td
					background-color: colors.$grey-15

			.mainRow td
				padding-top: 18px
				color: colors.$primary-100
				font-weight: 500

			.totalRow
				border-top: 2px solid colors.$grey-30

				td
					color: colors.$grey-60
					font-weight: 500

			.freeRow td
				color: colors.$primary-100
				font-weight: 500

				&.negative
					color: colors.$highlight-red-100

		.nameCol
			position: sticky
			left: 0
			z-index: 1
			min-width: 22ch
			background: colors.$black
			color: colors.$grey-85

			&.subName
				padding-left: 24px

		.valCol
			min-width: 9ch
			text-align: right !important
			font-variant-numeric: tabular-nums
			color: colors.$grey-85

		.owner
			font-size: 11px
			font-weight: normal
			text-transform: none
			letter-spacing: 0
			color: colors.$grey-50
</style>
